/**
 * Theme Manager Utility
 * Handles dark/light mode switching and persistence
 */
export class ThemeManager {
  private static readonly STORAGE_KEY = 'grassrootz-theme';
  
  /**
   * Check if dark mode is currently enabled
   */
  static isDarkMode(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) === 'dark';
  }
  
  /**
   * Set the theme mode and persist to localStorage
   */
  static setDarkMode(isDark: boolean): void {
    localStorage.setItem(this.STORAGE_KEY, isDark ? 'dark' : 'light');
    this.applyTheme(isDark);
  }
  
  /**
   * Apply the theme by adding/removing CSS class
   */
  static applyTheme(isDark: boolean): void {
    if (isDark) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }
  
  /**
   * Get theme from URL query parameters
   */
  static getThemeFromUrl(): 'dark' | 'light' | null {
    const urlParams = new URLSearchParams(window.location.search);
    const theme = urlParams.get('theme');
    
    if (theme === 'dark' || theme === 'light') {
      return theme;
    }
    
    return null;
  }
  
  /**
   * Initialize theme on app startup
   * Checks URL parameters first, then localStorage, finally defaults to light
   */
  static initializeTheme(): void {
    // First check for URL parameter
    const urlTheme = this.getThemeFromUrl();
    
    if (urlTheme) {
      // Set theme from URL parameter and persist to localStorage
      this.setDarkMode(urlTheme === 'dark');
      return;
    }
    
    // Fall back to localStorage
    const isDark = this.isDarkMode();
    this.applyTheme(isDark);
  }
  
  /**
   * Toggle between light and dark mode
   */
  static toggleTheme(): boolean {
    const newMode = !this.isDarkMode();
    this.setDarkMode(newMode);
    return newMode;
  }
}
